/**
 * Deploy-environment helpers.
 *
 * Both staging and prod are built with Vite's default `production` mode, so
 * `import.meta.env.MODE` is `'production'` in both and can't distinguish them.
 * The deploy workflow injects `VITE_APP_STAGE` ('production' | 'staging') as
 * the real source of truth. Locally it's undefined, which we treat as a dev
 * environment (staging-only features visible).
 */
export type AppStage = 'production' | 'staging' | 'development';

export function getAppStage(): AppStage {
  const raw = (import.meta.env.VITE_APP_STAGE ?? '').toString().toLowerCase();
  if (raw === 'production') return 'production';
  if (raw === 'staging') return 'staging';
  return 'development';
}

/** True everywhere except production (staging + local dev). */
export function isNonProd(): boolean {
  return getAppStage() !== 'production';
}

/**
 * The legacy PHP host (`html/` — Apache, `showfile_eform.php`, `/uimages`,
 * `/downloads`, the TreatRx OAuth callback) this app still links out to.
 * Prod and staging point at different hosts, so this can't be a hardcoded
 * string: the deploy workflow injects `VITE_LEGACY_APP_URL` per environment
 * (mirrors `VITE_APP_STAGE`, `VITE_CORP_AUTH_URL`). Locally / if unset,
 * falls back to production so links still resolve.
 */
export function getLegacyOrigin(): string {
  const raw = (import.meta.env.VITE_LEGACY_APP_URL ?? '').toString().trim();
  if (raw !== '') return raw.replace(/\/+$/, '');
  return getAppStage() === 'staging'
    ? 'https://nh.followthatpatient.com'
    : 'https://app.followthatpatient.com';
}
